import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  ActivatedRoute,
  Data,
  NavigationEnd,
  ParamMap,
  Router,
} from '@angular/router';
import { filter, Subject, switchMap, take, takeUntil } from 'rxjs';

import { EResourceViewMode } from 'app/shared/resource-view-mode.const';
import { LumResponseError } from 'v2/shared/api/lum-response-error';
import { LumSnackBar } from 'v2/shared/lum-snack-bar/lum-snack-bar.service';

import { ICostLayer } from '../../cost-layers.types';
import {
  CostLayerEditHelperService,
  ICostLayerEditHelperDone,
} from '../../services/cost-layer-edit-helper.service';
import { CostLayerEditDataStore } from '../../services/cost-layer-edit.data-store';
import { CostLayersDataStore } from '../../services/cost-layers.data-store';

//

@Component({
  selector: 'lum-cost-layer',
  templateUrl: './cost-layer.component.html',
  styleUrls: ['./cost-layer.component.scss'],
  providers: [
    CostLayerEditDataStore, // required by CostLayerEditHelperService
    CostLayerEditHelperService,
  ],
})
export class CostLayerComponent implements OnDestroy, OnInit {
  #destroy$ = new Subject<void>();

  readonly EResourceViewMode = EResourceViewMode;
  id :number = null;
  isEditing :boolean = false;
  viewMode :EResourceViewMode = EResourceViewMode.READ;

  constructor(
    private _route :ActivatedRoute,
    private _router :Router,
    private _snackBar :LumSnackBar,
    public editHelper :CostLayerEditHelperService,
    public store :CostLayersDataStore,
  ) {}

  ngOnInit() {
    this.#initEditHelper();
    this.#subscribeToRouteParams();
    this.#subscribeToChildRouteData();
  }
  ngOnDestroy() {
    this.#destroy$.next();
    this.#destroy$.complete();
  }

  handleClickRevert(e :PointerEvent) {
    e.preventDefault();

    this.editHelper.revertEdits();
  }
  handleClickSave(e :PointerEvent) {
    e.preventDefault();

    this.editHelper.save();
  }

  #initEditHelper() {
    const { viewingCostLayer, viewingCostLayerLoading } = this.store.getState();

    this.editHelper.setLayer(viewingCostLayer, {
      loading: viewingCostLayerLoading,
    });

    this.editHelper.done$
      .pipe(takeUntil(this.#destroy$))
      .subscribe((result :ICostLayerEditHelperDone) => {
        this._router.navigate(['/cost-layers', this.id]);
      });
  }

  #loadCostLayer() {
    this.store.clearViewingChanges();
    this.store.clearViewingCostLayer();

    this.editHelper.setLayer(null, { loading: true });

    this.store
      .loadLayer(this.id)
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: (layer :ICostLayer) => {
          this.editHelper.setLayer(layer);
        },
        error: (error :LumResponseError) => {
          this._snackBar.openError(error.message);
        },
      });
  }

  #subscribeToChildRouteData() {
    // falls back on this route's data as a failsafe
    const _getData = () => (this._route.firstChild ?? this._route).data;

    // initial load
    _getData()
      .pipe(take(1))
      .subscribe((data :Data) => {
        this.#updateViewMode(data.viewMode);
      });

    // Because `viewMode` is a property on the child routes, the `data`
    // observable doesn't fire on this route when the child changes.  So this
    // component instead needs to manually listen for navigation and update
    // from the child route's data at that point.
    this._router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        switchMap(() => _getData()),
        takeUntil(this.#destroy$),
      )
      .subscribe((data :Data) => {
        this.#updateViewMode(data.viewMode);
      });
  }
  #subscribeToRouteParams() {
    this._route.paramMap
      .pipe(takeUntil(this.#destroy$))
      .subscribe((params :ParamMap) => {
        const _id = params.get('id');
        this.id = _id ? parseInt(`${_id}`, 10) : null;

        if (!this.id || !Number.isFinite(this.id)) {
          console.warn(`Invalid cost layer id ${JSON.stringify(_id)}`);

          return;
        }

        // load the layer
        this.#loadCostLayer();
      });
  }

  #updateViewMode(viewMode :EResourceViewMode) {
    this.viewMode = viewMode;
    this.isEditing = [EResourceViewMode.CREATE, EResourceViewMode.UPDATE].includes(
      viewMode,
    );

    const viewingLayer = this.store.get('viewingCostLayer');

    if (this.isEditing && !this.editHelper.isEditingLayer(viewingLayer?.id)) {
      this.editHelper.setLayer(viewingLayer);
    }
  }
}
